/**
 * Input handling: keyboard, power slider, weapon select, fire button.
 */

import type { WeaponKey, PlayerLoadout, PlayerState, StructureState, AmmoCount, ObjectiveKey, ItemKey } from '../types';
import { WEAPONS, WEAPON_KEYS, JUMPPACK_FUEL_COST, DELIVERY_COST_MULTIPLIER, HEAL_ITEMS } from '../constants';
import { cur, weaponDisplayName, itemDisplayName } from './flavor';
import { getDeliveryCost, calculateShopTotal } from '../core/economy';
import { isCheatConsoleOpen } from './cheatConsole';
import { theme } from './theme';
import { thumbnails, thumbCanvasHtml } from '../rendering/modelThumbnails';

// =============================================================================
// CONTROL MODE — aim vs. move vs. jump
// =============================================================================

export type ControlMode = 'aim' | 'move' | 'jump' | 'build';

// =============================================================================
// AIM STATE
// =============================================================================

export interface AimState {
  azimuth: number;
  elevation: number;
  power: number;
  weaponIndex: number;
  mode: ControlMode;
  // Jump-specific state
  jumpPower: number;         // 0-100, affects launch velocity
  jumpAzimuth: number;       // aim direction for manual aiming (future)
  jumpElevation: number;     // launch angle for manual aiming (future)
}

export function createAimState(): AimState {
  return {
    azimuth: 0,
    elevation: Math.PI / 4,
    power: 50,
    weaponIndex: 0,
    mode: 'aim',
    jumpPower: 50,
    jumpAzimuth: 0,
    jumpElevation: Math.PI / 4,
  };
}

// =============================================================================
// KEYBOARD INPUT
// =============================================================================

export interface KeyState {
  [key: string]: boolean;
}

export function createKeyState(): KeyState {
  return {};
}

export function setupKeyboardInput(
  keyState: KeyState,
  onTabPress: (reverse: boolean) => void,
  onFirePress: () => void,
  onMoveToggle: () => void,
  onJumpToggle: () => void,
  onMelee: () => void,
  onShopToggle?: () => void,
  onBuildToggle?: () => void,
  onCameraToggle?: () => void,
  onYieldTurn?: () => void,
  onHelpToggle?: () => void,
  onSettingsToggle?: () => void,
  onCommsToggle?: () => void,
  onPlantFlag?: () => void,
  onResetCamera?: () => void,
  onLeadershipToggle?: () => void,
  onRemoveStake?: () => void,
  onPickup?: () => void,
  onInteract?: () => void,
  onDeliver?: () => void,
  onDrop?: () => void,
  onHeal?: () => void,
  onInventoryToggle?: () => void,
  onWeaponCycle?: () => void,
  onWeaponSelect?: (slot: number) => void,
): () => void {
  const onKeyDown = (e: KeyboardEvent) => {
    if (isCheatConsoleOpen()) return;
    keyState[e.key.toLowerCase()] = true;

    if (e.key === 'Tab') {
      e.preventDefault();
      onTabPress(e.shiftKey);
    }

    if (e.key.toLowerCase() === 'z' && onWeaponCycle) {
      onWeaponCycle();
    }

    if (onWeaponSelect && e.key >= '1' && e.key <= '9' && !e.ctrlKey && !e.metaKey && !e.altKey) {
      onWeaponSelect(parseInt(e.key, 10) - 1);
    }

    if (e.key === 'Enter' || e.key === ' ') {
      onFirePress();
    }

    if (e.key.toLowerCase() === 'm') {
      onMoveToggle();
    }

    if (e.key.toLowerCase() === 'j') {
      onJumpToggle();
    }

    if (e.key.toLowerCase() === 'x') {
      onMelee();
    }

    if (e.key.toLowerCase() === 'b' && onShopToggle) {
      onShopToggle();
    }

    if (e.key.toLowerCase() === 'n' && onBuildToggle) {
      onBuildToggle();
    }

    if (e.key.toLowerCase() === 'v' && onCameraToggle) {
      onCameraToggle();
    }

    if (e.key.toLowerCase() === 'y' && onYieldTurn) {
      onYieldTurn();
    }

    if (e.key.toLowerCase() === 'h' && onHelpToggle) {
      onHelpToggle();
    }

    if (e.key === 'Escape' && onSettingsToggle) {
      onSettingsToggle();
    }

    if (e.key.toLowerCase() === 'l' && onCommsToggle) {
      onCommsToggle();
    }

    if (e.key.toLowerCase() === 'f' && onPlantFlag) {
      onPlantFlag();
    }

    if (e.key.toLowerCase() === 'c' && onResetCamera) {
      onResetCamera();
    }

    if (e.key.toLowerCase() === 't' && onLeadershipToggle) {
      onLeadershipToggle();
    }

    if (e.key.toLowerCase() === 'r' && onRemoveStake) {
      onRemoveStake();
    }

    if (e.key.toLowerCase() === 'g' && onPickup) {
      onPickup();
    }

    if (e.key.toLowerCase() === 'i') {
      const interactBtn = document.getElementById('interact-btn');
      const interactVisible = interactBtn && !interactBtn.classList.contains('hidden');
      if (interactVisible && onInteract) {
        onInteract();
      } else if (onInventoryToggle) {
        onInventoryToggle();
      }
    }

    if (e.key.toLowerCase() === 'u' && onDeliver) {
      onDeliver();
    }

    if (e.key.toLowerCase() === 'p' && onDrop) {
      onDrop();
    }

    if (e.key.toLowerCase() === 'k' && onHeal) {
      onHeal();
    }
  };

  const onKeyUp = (e: KeyboardEvent) => {
    keyState[e.key.toLowerCase()] = false;
  };

  window.addEventListener('keydown', onKeyDown);
  window.addEventListener('keyup', onKeyUp);

  return () => {
    window.removeEventListener('keydown', onKeyDown);
    window.removeEventListener('keyup', onKeyUp);
  };
}

export function processAimInput(aim: AimState, keys: KeyState, dt: number): boolean {
  let changed = false;
  const fine = keys['shift'] ? 0.1 : 1;
  const aimSpeed = 1.5 * dt * fine;
  const powerSpeed = 45 * dt * fine;

  // Hitscan weapons (railgun) can depress below horizon
  const currentWeapon = WEAPONS[WEAPON_KEYS[aim.weaponIndex]];
  const minElevation = currentWeapon?.hitscan ? -Math.PI / 4 : 0.05;

  if (keys['a'] || keys['arrowleft']) {
    aim.azimuth -= aimSpeed;
    changed = true;
  }
  if (keys['d'] || keys['arrowright']) {
    aim.azimuth += aimSpeed;
    changed = true;
  }
  if (keys['w'] || keys['arrowup']) {
    aim.elevation = Math.min(Math.PI / 2 - 0.02, aim.elevation + aimSpeed * 0.5);
    changed = true;
  }
  if (keys['s'] || keys['arrowdown']) {
    aim.elevation = Math.max(minElevation, aim.elevation - aimSpeed * 0.5);
    changed = true;
  }
  if (keys['q']) {
    aim.power = Math.max(5, aim.power - powerSpeed);
    changed = true;
  }
  if (keys['e']) {
    aim.power = Math.min(100, aim.power + powerSpeed);
    changed = true;
  }

  return changed;
}

// =============================================================================
// AIM PAD — drag the aim sphere to aim (touch and mouse)
// =============================================================================

const AIM_PAD_AZIMUTH_PER_PX = 0.012;
const AIM_PAD_ELEVATION_PER_PX = 0.008;

/**
 * Dragging on the aim sphere turns azimuth (left/right) and elevation (up/down),
 * the touch equivalent of WASD. Uses pointer events so a mouse works too.
 * `onChange` fires after every change with the same aim object.
 */
export function setupAimPad(canvas: HTMLCanvasElement, aim: AimState, onChange: () => void): () => void {
  let dragging = false;
  let last = { x: 0, y: 0 };
  const onDown = (e: PointerEvent) => {
    dragging = true;
    last = { x: e.clientX, y: e.clientY };
    canvas.setPointerCapture(e.pointerId);
    e.preventDefault();
  };
  const onMove = (e: PointerEvent) => {
    if (!dragging) return;
    const dx = e.clientX - last.x;
    const dy = e.clientY - last.y;
    last = { x: e.clientX, y: e.clientY };
    if (dx === 0 && dy === 0) return;
    const currentWeapon = WEAPONS[WEAPON_KEYS[aim.weaponIndex]];
    const minElevation = currentWeapon?.hitscan ? -Math.PI / 4 : 0.05;
    aim.azimuth += dx * AIM_PAD_AZIMUTH_PER_PX;
    aim.elevation = Math.max(minElevation, Math.min(Math.PI / 2 - 0.02, aim.elevation - dy * AIM_PAD_ELEVATION_PER_PX));
    onChange();
    e.preventDefault();
  };
  const onUp = (e: PointerEvent) => {
    dragging = false;
    try { canvas.releasePointerCapture(e.pointerId); } catch { /* already released */ }
  };
  canvas.addEventListener('pointerdown', onDown);
  canvas.addEventListener('pointermove', onMove);
  canvas.addEventListener('pointerup', onUp);
  canvas.addEventListener('pointercancel', onUp);
  return () => {
    canvas.removeEventListener('pointerdown', onDown);
    canvas.removeEventListener('pointermove', onMove);
    canvas.removeEventListener('pointerup', onUp);
    canvas.removeEventListener('pointercancel', onUp);
  };
}

// =============================================================================
// WEAPON BUTTONS — collapsible 2-row layout
// =============================================================================

const BOTTOM_ROW_MAX = 6;
let weaponGridExpanded = false;
let storedOnSelect: ((index: number) => void) | null = null;

let currentAllowedWeapons: WeaponKey[] | undefined;

export function buildWeaponButtons(
  onSelect: (index: number) => void,
  loadout?: PlayerLoadout,
  allowedWeapons?: WeaponKey[]
): void {
  storedOnSelect = onSelect;
  currentAllowedWeapons = allowedWeapons;
  const container = document.getElementById('weapon-btns');
  if (!container) return;

  container.innerHTML = '';

  // Top row (expandable, hidden by default)
  const topRow = document.createElement('div');
  topRow.id = 'weapon-row-top';
  topRow.style.cssText = 'display:none;flex-wrap:wrap;gap:4px;';

  // Bottom container: bottom row + toggle
  const bottomContainer = document.createElement('div');
  bottomContainer.style.cssText = 'display:flex;gap:4px;align-items:center;';

  const bottomRow = document.createElement('div');
  bottomRow.id = 'weapon-row-bottom';
  bottomRow.style.cssText = 'display:flex;flex-wrap:wrap;gap:4px;';

  const toggle = document.createElement('button');
  toggle.id = 'weapon-expand-toggle';
  toggle.className = 'weapon-expand-toggle';
  toggle.style.display = 'none';
  toggle.onclick = () => {
    weaponGridExpanded = !weaponGridExpanded;
    topRow.style.display = weaponGridExpanded ? 'flex' : 'none';
    updateExpandToggle();
  };

  bottomContainer.appendChild(bottomRow);
  bottomContainer.appendChild(toggle);
  container.appendChild(topRow);
  container.appendChild(bottomContainer);

  // Create all buttons (filter by allowed weapons — standard always allowed)
  const visibleKeys = allowedWeapons
    ? WEAPON_KEYS.filter(k => k === 'standard' || allowedWeapons.includes(k))
    : WEAPON_KEYS;
  visibleKeys.forEach((key, visIdx) => {
    const weapon = WEAPONS[key];
    const ammo = loadout?.ammo[key];
    const ammoStr = formatAmmoCount(ammo);
    const isEmpty = ammo !== null && ammo !== undefined && ammo <= 0;
    // Use real WEAPON_KEYS index so weaponIndex maps correctly
    const realIdx = WEAPON_KEYS.indexOf(key);

    const btn = document.createElement('button');
    btn.className = 'weapon-btn' + (visIdx === 0 ? ' active' : '') + (isEmpty ? ' disabled' : '');
    btn.innerHTML = `${weapon.icon} ${weapon.name} <span style="opacity:0.5;font-family:'JetBrains Mono',monospace;font-size:10px;">${ammoStr}</span>`;
    btn.dataset.weaponIndex = String(realIdx);
    btn.dataset.weaponKey = key;
    btn.onclick = () => {
      if (!isEmpty) onSelect(realIdx);
    };

    // Place into correct row
    if (!isEmpty && bottomRow.children.length < BOTTOM_ROW_MAX) {
      bottomRow.appendChild(btn);
    } else {
      topRow.appendChild(btn);
    }
  });

  updateExpandToggle();
}

export function updateWeaponButtons(selectedIndex: number, loadout?: PlayerLoadout): void {
  const topRow = document.getElementById('weapon-row-top');
  const bottomRow = document.getElementById('weapon-row-bottom');
  if (!topRow || !bottomRow) return;

  const buttons = document.querySelectorAll('.weapon-btn') as NodeListOf<HTMLButtonElement>;

  // Classify buttons into bottom vs top
  const bottomBtns: HTMLButtonElement[] = [];
  const topBtns: HTMLButtonElement[] = [];

  buttons.forEach((btn) => {
    const idx = parseInt(btn.dataset.weaponIndex!, 10);
    const key = btn.dataset.weaponKey as WeaponKey;
    const ammo = loadout?.ammo[key];
    const isEmpty = ammo !== null && ammo !== undefined && ammo <= 0;

    // Update content and classes
    const weapon = WEAPONS[WEAPON_KEYS[idx]];
    const ammoStr = formatAmmoCount(ammo);
    btn.innerHTML = `${weapon.icon} ${weapon.name} <span style="opacity:0.5;font-family:'JetBrains Mono',monospace;font-size:10px;">${ammoStr}</span>`;
    btn.classList.toggle('active', idx === selectedIndex && !isEmpty);
    btn.classList.toggle('disabled', isEmpty);

    // Re-attach click handler in case onSelect changed
    btn.onclick = () => {
      if (!isEmpty && storedOnSelect) storedOnSelect(idx);
    };

    if (!isEmpty) {
      bottomBtns.push(btn);
    } else {
      topBtns.push(btn);
    }
  });

  // Selected weapon exception: always keep it in bottom row
  const selectedBtn = Array.from(buttons).find(b => parseInt(b.dataset.weaponIndex!, 10) === selectedIndex);
  if (selectedBtn && topBtns.includes(selectedBtn)) {
    // Move selected from top to bottom
    topBtns.splice(topBtns.indexOf(selectedBtn), 1);
    bottomBtns.push(selectedBtn);
  }

  // Enforce max 6 in bottom: overflow goes to top
  // Sort bottom by weapon index first to maintain order
  bottomBtns.sort((a, b) => parseInt(a.dataset.weaponIndex!, 10) - parseInt(b.dataset.weaponIndex!, 10));

  while (bottomBtns.length > BOTTOM_ROW_MAX) {
    // Remove last non-selected button
    let removed = false;
    for (let i = bottomBtns.length - 1; i >= 0; i--) {
      if (parseInt(bottomBtns[i].dataset.weaponIndex!, 10) !== selectedIndex) {
        topBtns.push(bottomBtns.splice(i, 1)[0]);
        removed = true;
        break;
      }
    }
    if (!removed) break; // safety: all are selected (impossible)
  }

  // Sort both rows by weapon index for stable order
  topBtns.sort((a, b) => parseInt(a.dataset.weaponIndex!, 10) - parseInt(b.dataset.weaponIndex!, 10));
  bottomBtns.sort((a, b) => parseInt(a.dataset.weaponIndex!, 10) - parseInt(b.dataset.weaponIndex!, 10));

  // Move DOM elements
  bottomBtns.forEach(btn => bottomRow.appendChild(btn));
  topBtns.forEach(btn => topRow.appendChild(btn));

  updateExpandToggle();
}

function updateExpandToggle(): void {
  const topRow = document.getElementById('weapon-row-top');
  const toggle = document.getElementById('weapon-expand-toggle');
  if (!topRow || !toggle) return;

  const topCount = topRow.children.length;
  if (topCount === 0) {
    toggle.style.display = 'none';
    topRow.style.display = 'none';
    weaponGridExpanded = false;
  } else {
    toggle.style.display = '';
    toggle.textContent = weaponGridExpanded ? '\u25BE' : `\u25B4 +${topCount}`;
    topRow.style.display = weaponGridExpanded ? 'flex' : 'none';
  }
}

function formatAmmoCount(ammo: AmmoCount | undefined): string {
  if (ammo === null || ammo === undefined) return '∞';
  return `×${ammo}`;
}

export function getSelectedWeaponKey(index: number): WeaponKey {
  return WEAPON_KEYS[index];
}

// =============================================================================
// POWER SLIDER
// =============================================================================

export function setupPowerSlider(
  onChange: (power: number) => void
): () => void {
  const slider = document.getElementById('power-slider') as HTMLInputElement | null;
  if (!slider) return () => {};

  const handler = (e: Event) => {
    const value = parseFloat((e.target as HTMLInputElement).value);
    onChange(value);
  };

  slider.addEventListener('input', handler);

  return () => {
    slider.removeEventListener('input', handler);
  };
}

// =============================================================================
// FIRE BUTTON
// =============================================================================

export function setupFireButton(onFire: () => void): () => void {
  const btn = document.getElementById('fire-btn');
  if (!btn) return () => {};

  btn.addEventListener('click', onFire);

  return () => {
    btn.removeEventListener('click', onFire);
  };
}

export function updateFireButtonText(text: string): void {
  const btn = document.getElementById('fire-btn');
  if (btn) {
    btn.textContent = text;
  }
}

export function setFireButtonEnabled(enabled: boolean): void {
  const btn = document.getElementById('fire-btn') as HTMLButtonElement | null;
  if (btn) {
    btn.disabled = !enabled;
    btn.style.opacity = enabled ? '' : '0.4';
  }
}

// =============================================================================
// MOVE BUTTON
// =============================================================================

export function setupMoveButton(onToggle: () => void): () => void {
  const btn = document.getElementById('move-btn');
  if (!btn) return () => {};

  btn.addEventListener('click', onToggle);

  return () => {
    btn.removeEventListener('click', onToggle);
  };
}

export function updateMoveButton(isMoving: boolean): void {
  const btn = document.getElementById('move-btn');
  if (btn) {
    btn.classList.toggle('active', isMoving);
    btn.textContent = isMoving ? 'DONE (M)' : 'WALK (M)';
  }
}

// =============================================================================
// JUMP BUTTON
// =============================================================================

export function setupJumpButton(onToggle: () => void): () => void {
  const btn = document.getElementById('jump-btn');
  if (!btn) return () => {};

  btn.addEventListener('click', onToggle);

  return () => {
    btn.removeEventListener('click', onToggle);
  };
}

export function updateJumpButton(isJumping: boolean, canAfford: boolean, fuel?: number): void {
  const btn = document.getElementById('jump-btn');
  if (btn) {
    btn.classList.toggle('active', isJumping);
    btn.classList.toggle('disabled', !canAfford && !isJumping);
    if (isJumping) {
      btn.textContent = 'CANCEL (J)';
    } else {
      btn.textContent = fuel && fuel > 0 ? `JUMP (J) [${fuel}F]` : 'JUMP (J)';
    }
  }
}

// =============================================================================
// MELEE BUTTON
// =============================================================================

export function setupMeleeButton(onMelee: () => void): () => void {
  const btn = document.getElementById('melee-btn');
  if (!btn) return () => {};

  btn.addEventListener('click', onMelee);

  return () => {
    btn.removeEventListener('click', onMelee);
  };
}

export function updateMeleeButton(visible: boolean, targetName?: string): void {
  const btn = document.getElementById('melee-btn');
  if (btn) {
    btn.classList.toggle('hidden', !visible);
    if (visible && targetName) {
      btn.textContent = `PUNCH ${targetName} (X)`;
    } else {
      btn.textContent = 'PUNCH (X)';
    }
  }
}

// =============================================================================
// YIELD BUTTON
// =============================================================================

export function setupYieldButton(onYield: () => void): () => void {
  const btn = document.getElementById('yield-btn');
  if (!btn) return () => {};

  btn.addEventListener('click', onYield);

  return () => {
    btn.removeEventListener('click', onYield);
  };
}

// =============================================================================
// HEAL BUTTON
// =============================================================================

export function setupHealButton(onHeal: () => void): () => void {
  const btn = document.getElementById('heal-btn');
  if (!btn) return () => {};

  btn.addEventListener('click', onHeal);

  return () => {
    btn.removeEventListener('click', onHeal);
  };
}

export function updateHealButton(visible: boolean, movementOk: boolean, label?: string): void {
  const btn = document.getElementById('heal-btn');
  if (!btn) return;
  btn.classList.toggle('hidden', !visible);
  btn.classList.toggle('disabled', !movementOk);
  if (label) {
    btn.textContent = label;
  } else {
    btn.textContent = 'HEAL (K)';
  }
}

export function updateMovementDisplay(remaining: number): void {
  const el = document.getElementById('val-movement');
  if (el) {
    el.textContent = remaining.toFixed(1);
  }
}

// =============================================================================
// IN-GAME SHOP
// =============================================================================

// Purchasable weapons (exclude standard which is unlimited/free)
const INGAME_PURCHASABLE: WeaponKey[] = ['heavy', 'cluster', 'nuke', 'probe', 'punch', 'antigrav', 'gravBomb', 'tether', 'terraformer', 'emp', 'drill', 'fire'];

interface InGameShopState {
  playerCbp: number;
  purchases: Partial<Record<WeaponKey, number>>;
  fuelQty: number;
  itemPurchases: Partial<Record<ItemKey, number>>;
}

let inGameShopState: InGameShopState | null = null;

export function showInGameShop(player: PlayerState, allowedWeapons?: WeaponKey[]): void {
  currentAllowedWeapons = allowedWeapons;
  inGameShopState = {
    playerCbp: player.cbp,
    purchases: {},
    fuelQty: 0,
    itemPurchases: {},
  };
  renderInGameShopItems();
  updateInGameShopCost();

  const overlay = document.getElementById('ingame-shop-overlay');
  if (overlay) overlay.classList.remove('hidden');
}

export function hideInGameShop(): void {
  inGameShopState = null;
  thumbnails.clear();
  const overlay = document.getElementById('ingame-shop-overlay');
  if (overlay) overlay.classList.add('hidden');
}

export function isInGameShopOpen(): boolean {
  return inGameShopState !== null;
}

export function getInGameShopPurchases(): Partial<Record<WeaponKey, number>> {
  return inGameShopState?.purchases ?? {};
}

export function getInGameShopFuelQty(): number {
  return inGameShopState?.fuelQty ?? 0;
}

export function getInGameShopItemPurchases(): Partial<Record<ItemKey, number>> {
  return inGameShopState?.itemPurchases ?? {};
}

function renderInGameShopItems(): void {
  if (!inGameShopState) return;
  const itemsEl = document.getElementById('ingame-shop-items');
  if (!itemsEl) return;

  const spent = getInGameShopSpent();
  const remaining = inGameShopState.playerCbp - spent;

  const shopWeapons = currentAllowedWeapons
    ? INGAME_PURCHASABLE.filter(k => currentAllowedWeapons!.includes(k))
    : INGAME_PURCHASABLE;

  itemsEl.innerHTML = '';
  for (const key of shopWeapons) {
    const weapon = WEAPONS[key];
    const deliveryCost = getDeliveryCost(key);
    const count = inGameShopState.purchases[key] || 0;
    const canAfford = remaining >= deliveryCost;

    const row = document.createElement('div');
    row.style.cssText = `display:flex; align-items:center; gap:12px; padding:10px 12px; background:${theme.rowBg}; border-radius:8px;`;

    row.innerHTML = `
      ${thumbCanvasHtml(`weapon:${key}`, 40, `background:${theme.chipBg}; border-radius:6px; flex-shrink:0;`)}
      <div style="flex:1;">
        <div style="font-family:'Space Grotesk',sans-serif; font-size:13px; font-weight:500;">${weaponDisplayName(key)}</div>
        <div style="font-family:'JetBrains Mono',monospace; font-size:10px; opacity:0.4;">
          <span style="text-decoration:line-through; opacity:0.5;">${weapon.cbpCost}</span>
          <span style="color:${theme.delivery}; font-weight:500;"> ${deliveryCost} ${cur()}</span>
          each • DMG ${weapon.damage} • R ${weapon.blastRadius.toFixed(1)}
        </div>
      </div>
      <div style="display:flex; align-items:center; gap:8px;">
        <button class="ingame-shop-btn minus" data-weapon="${key}" style="width:28px; height:28px; border:1px solid ${theme.btnMinus.border}; background:${theme.btnMinus.bg}; border-radius:4px; color:${theme.btnMinus.text}; cursor:pointer; font-size:16px;">−</button>
        <span style="font-family:'JetBrains Mono',monospace; font-size:16px; min-width:24px; text-align:center;">${count}</span>
        <button class="ingame-shop-btn plus" data-weapon="${key}" ${!canAfford ? 'disabled' : ''} style="width:28px; height:28px; border:1px solid ${canAfford ? theme.btnPositive.border : 'rgba(255,255,255,0.1)'}; background:${canAfford ? theme.btnPositive.bg : theme.rowBgSelected}; border-radius:4px; color:${canAfford ? theme.btnPositive.text : theme.btnDisabled.text}; cursor:${canAfford ? 'pointer' : 'not-allowed'}; font-size:16px;">+</button>
      </div>
    `;

    itemsEl.appendChild(row);
  }

  // Fuel row
  const fuelDeliveryCost = JUMPPACK_FUEL_COST * DELIVERY_COST_MULTIPLIER;
  const fuelCount = inGameShopState.fuelQty;
  const canAffordFuel = remaining >= fuelDeliveryCost;
  const fuelRow = document.createElement('div');
  fuelRow.style.cssText = `grid-column:1/-1; display:flex; align-items:center; gap:12px; padding:10px 12px; background:${theme.rowBg}; border-radius:8px;`;
  fuelRow.innerHTML = `
    <span style="font-size:18px; width:24px; text-align:center;">&#x26A1;</span>
    <div style="flex:1;">
      <div style="font-family:'Space Grotesk',sans-serif; font-size:13px; font-weight:500;">Jumppack Fuel</div>
      <div style="font-family:'JetBrains Mono',monospace; font-size:10px; opacity:0.4;">
        <span style="text-decoration:line-through; opacity:0.5;">${JUMPPACK_FUEL_COST}</span>
        <span style="color:${theme.delivery}; font-weight:500;"> ${fuelDeliveryCost} ${cur()}</span>
        each • +${15} velocity per jump
      </div>
    </div>
    <div style="display:flex; align-items:center; gap:8px;">
      <button class="ingame-fuel-btn minus" style="width:28px; height:28px; border:1px solid ${theme.btnMinus.border}; background:${theme.btnMinus.bg}; border-radius:4px; color:${theme.btnMinus.text}; cursor:pointer; font-size:16px;">−</button>
      <span style="font-family:'JetBrains Mono',monospace; font-size:16px; min-width:24px; text-align:center;">${fuelCount}</span>
      <button class="ingame-fuel-btn plus" ${!canAffordFuel ? 'disabled' : ''} style="width:28px; height:28px; border:1px solid ${canAffordFuel ? theme.btnPositive.border : 'rgba(255,255,255,0.1)'}; background:${canAffordFuel ? theme.btnPositive.bg : theme.rowBgSelected}; border-radius:4px; color:${canAffordFuel ? theme.btnPositive.text : theme.btnDisabled.text}; cursor:${canAffordFuel ? 'pointer' : 'not-allowed'}; font-size:16px;">+</button>
    </div>
  `;
  itemsEl.appendChild(fuelRow);

  // Heal item rows
  for (const itemKey of ['healSmall', 'healLarge'] as ItemKey[]) {
    const item = HEAL_ITEMS[itemKey];
    const deliveryCost = Math.ceil(item.cbpCost * DELIVERY_COST_MULTIPLIER);
    const count = inGameShopState.itemPurchases[itemKey] || 0;
    const canAffordItem = remaining >= deliveryCost;
    const itemRow = document.createElement('div');
    itemRow.style.cssText = `display:flex; align-items:center; gap:12px; padding:10px 12px; background:${theme.healRowBg}; border-radius:8px;`;
    itemRow.innerHTML = `
      <span style="font-size:18px; width:24px; text-align:center;">${itemKey === 'healSmall' ? '✚' : '✛'}</span>
      <div style="flex:1;">
        <div style="font-family:'Space Grotesk',sans-serif; font-size:13px; font-weight:500; color:${theme.heal};">${itemDisplayName(itemKey)}</div>
        <div style="font-family:'JetBrains Mono',monospace; font-size:10px; opacity:0.4;">
          <span style="text-decoration:line-through; opacity:0.5;">${item.cbpCost}</span>
          <span style="color:${theme.delivery}; font-weight:500;"> ${deliveryCost} ${cur()}</span>
          each • +${item.healPercent}% HP
        </div>
      </div>
      <div style="display:flex; align-items:center; gap:8px;">
        <button class="ingame-heal-btn minus" data-item="${itemKey}" style="width:28px; height:28px; border:1px solid ${theme.btnMinus.border}; background:${theme.btnMinus.bg}; border-radius:4px; color:${theme.btnMinus.text}; cursor:pointer; font-size:16px;">−</button>
        <span style="font-family:'JetBrains Mono',monospace; font-size:16px; min-width:24px; text-align:center;">${count}</span>
        <button class="ingame-heal-btn plus" data-item="${itemKey}" ${!canAffordItem ? 'disabled' : ''} style="width:28px; height:28px; border:1px solid ${canAffordItem ? theme.btnHeal.border : 'rgba(255,255,255,0.1)'}; background:${canAffordItem ? theme.btnHeal.bg : theme.rowBgSelected}; border-radius:4px; color:${canAffordItem ? theme.btnHeal.text : theme.btnDisabled.text}; cursor:${canAffordItem ? 'pointer' : 'not-allowed'}; font-size:16px;">+</button>
      </div>
    `;
    itemsEl.appendChild(itemRow);
  }

  // Add click handlers
  itemsEl.querySelectorAll('.ingame-shop-btn').forEach(btn => {
    btn.addEventListener('click', (e) => {
      const target = e.currentTarget as HTMLButtonElement;
      const weaponKey = target.dataset.weapon as WeaponKey;
      const isPlus = target.classList.contains('plus');
      handleInGameShopAdjust(weaponKey, isPlus ? 1 : -1);
    });
  });

  // Fuel click handlers
  itemsEl.querySelectorAll('.ingame-fuel-btn').forEach(btn => {
    btn.addEventListener('click', (e) => {
      const target = e.currentTarget as HTMLButtonElement;
      const isPlus = target.classList.contains('plus');
      handleInGameFuelAdjust(isPlus ? 1 : -1);
    });
  });

  // Heal item click handlers
  itemsEl.querySelectorAll('.ingame-heal-btn').forEach(btn => {
    btn.addEventListener('click', (e) => {
      const target = e.currentTarget as HTMLButtonElement;
      const itemKey = target.dataset.item as ItemKey;
      const isPlus = target.classList.contains('plus');
      handleInGameHealAdjust(itemKey, isPlus ? 1 : -1);
    });
  });
  thumbnails.mount(itemsEl);
}

function getInGameShopSpent(): number {
  if (!inGameShopState) return 0;
  const fuelDeliveryCost = JUMPPACK_FUEL_COST * DELIVERY_COST_MULTIPLIER;
  let itemCost = 0;
  for (const key of Object.keys(inGameShopState.itemPurchases) as ItemKey[]) {
    const count = inGameShopState.itemPurchases[key] ?? 0;
    itemCost += count * HEAL_ITEMS[key].cbpCost * DELIVERY_COST_MULTIPLIER;
  }
  return calculateShopTotal(inGameShopState.purchases) + inGameShopState.fuelQty * fuelDeliveryCost + itemCost;
}

function handleInGameShopAdjust(weaponKey: WeaponKey, delta: number): void {
  if (!inGameShopState) return;

  const currentCount = inGameShopState.purchases[weaponKey] || 0;
  const newCount = Math.max(0, currentCount + delta);

  if (delta > 0) {
    const remaining = inGameShopState.playerCbp - getInGameShopSpent();
    if (remaining < getDeliveryCost(weaponKey)) return;
  }

  inGameShopState.purchases[weaponKey] = newCount;
  renderInGameShopItems();
  updateInGameShopCost();
}

function handleInGameFuelAdjust(delta: number): void {
  if (!inGameShopState) return;
  const fuelDeliveryCost = JUMPPACK_FUEL_COST * DELIVERY_COST_MULTIPLIER;
  const newQty = Math.max(0, inGameShopState.fuelQty + delta);

  if (delta > 0) {
    const remaining = inGameShopState.playerCbp - getInGameShopSpent();
    if (remaining < fuelDeliveryCost) return;
  }

  inGameShopState.fuelQty = newQty;
  renderInGameShopItems();
  updateInGameShopCost();
}

function handleInGameHealAdjust(itemKey: ItemKey, delta: number): void {
  if (!inGameShopState) return;
  const deliveryCost = Math.ceil(HEAL_ITEMS[itemKey].cbpCost * DELIVERY_COST_MULTIPLIER);
  const currentCount = inGameShopState.itemPurchases[itemKey] || 0;
  const newCount = Math.max(0, currentCount + delta);

  if (delta > 0) {
    const remaining = inGameShopState.playerCbp - getInGameShopSpent();
    if (remaining < deliveryCost) return;
  }

  inGameShopState.itemPurchases[itemKey] = newCount;
  renderInGameShopItems();
  updateInGameShopCost();
}

function updateInGameShopCost(): void {
  if (!inGameShopState) return;
  const cost = getInGameShopSpent();
  const remaining = inGameShopState.playerCbp - cost;

  const costEl = document.getElementById('ingame-shop-cost');
  const remainEl = document.getElementById('ingame-shop-remaining');
  if (costEl) costEl.textContent = String(cost);
  if (remainEl) remainEl.textContent = String(remaining);
}

export function setupShopButton(onOpen: () => void): () => void {
  const btn = document.getElementById('shop-btn');
  if (!btn) return () => {};

  btn.addEventListener('click', onOpen);
  return () => btn.removeEventListener('click', onOpen);
}

// =============================================================================
// BUILD MENU
// =============================================================================

import { canBuildEcotourism } from '../core/economy';

let buildMenuOpen = false;
let buildMenuCallback: ((type: string) => void) | null = null;

export function showBuildMenu(player: PlayerState, structures: StructureState[], onSelect: (type: string) => void, objective?: ObjectiveKey): void {
  buildMenuOpen = true;
  buildMenuCallback = onSelect;
  renderBuildMenuItems(player, structures, objective);
  const overlay = document.getElementById('build-menu-overlay');
  if (overlay) overlay.classList.remove('hidden');
}

export function hideBuildMenu(): void {
  buildMenuOpen = false;
  buildMenuCallback = null;
  const overlay = document.getElementById('build-menu-overlay');
  if (overlay) overlay.classList.add('hidden');
}

export function isBuildMenuOpen(): boolean {
  return buildMenuOpen;
}

function createBuildRow(
  icon: string,
  label: string,
  desc: string,
  canBuild: boolean,
  statusText: string,
  buildType: string
): HTMLDivElement {
  const row = document.createElement('div');
  row.style.cssText = `display:flex; align-items:center; gap:12px; padding:10px 12px; background:${theme.rowBg}; border-radius:8px; cursor:${canBuild ? 'pointer' : 'not-allowed'};`;

  row.innerHTML = `
    <span style="font-size:18px; width:24px; text-align:center;">${icon}</span>
    <div style="flex:1;">
      <div style="font-family:'Space Grotesk',sans-serif; font-size:13px; font-weight:500; color:${canBuild ? theme.text.primary : theme.text.muted};">${label}</div>
      <div style="font-family:'JetBrains Mono',monospace; font-size:10px; opacity:0.4;">
        ${desc}
      </div>
    </div>
    <div style="font-family:'JetBrains Mono',monospace; font-size:11px; opacity:${canBuild ? '0.7' : '0.3'};">
      ${statusText}
    </div>
  `;

  if (canBuild) {
    row.addEventListener('click', () => {
      if (buildMenuCallback) buildMenuCallback(buildType);
    });
    row.addEventListener('mouseenter', () => {
      row.style.background = theme.buildHoverBg;
    });
    row.addEventListener('mouseleave', () => {
      row.style.background = theme.rowBg;
    });
  }

  return row;
}

function renderBuildMenuItems(player: PlayerState, structures: StructureState[], objective?: ObjectiveKey): void {
  const itemsEl = document.getElementById('build-menu-items');
  if (!itemsEl) return;

  itemsEl.innerHTML = '';

  // Ecotourism center (available in all modes)
  const canBuildEco = canBuildEcotourism(player.id, structures);
  itemsEl.appendChild(createBuildRow(
    '\u{1F3E8}', 'Ecotourism Center',
    `+1 ${cur()}/turn income \u00B7 HP 20 \u00B7 Limit 1 \u00B7 Costs 4 movement`,
    canBuildEco,
    canBuildEco ? 'FREE' : 'LIMIT REACHED',
    'ecotourism'
  ));

  // Antenna (signal trace objective only)
  if (objective === 'signalTrace') {
    const hasEnoughMovement = player.movementRemaining >= 1.0;
    itemsEl.appendChild(createBuildRow(
      '\u{1F4E1}', 'Listening Post Antenna',
      'Buildable listening post \u00B7 HP 20 \u00B7 Costs 1 movement',
      hasEnoughMovement,
      hasEnoughMovement ? `0 ${cur()}` : 'NO MOVEMENT',
      'antenna'
    ));
  }
}

export function setupBuildButton(onOpen: () => void): () => void {
  const btn = document.getElementById('build-btn');
  if (!btn) return () => {};

  btn.addEventListener('click', onOpen);
  return () => btn.removeEventListener('click', onOpen);
}

export function updateBuildButton(isBuilding: boolean): void {
  const btn = document.getElementById('build-btn');
  if (btn) {
    btn.classList.toggle('active', isBuilding);
  }
}

// =============================================================================
// FLAG BUTTON
// =============================================================================

export function setupFlagButton(onPlantFlag: () => void): () => void {
  const btn = document.getElementById('flag-btn');
  if (!btn) return () => {};

  btn.addEventListener('click', onPlantFlag);
  return () => btn.removeEventListener('click', onPlantFlag);
}

export function updateFlagButton(canPlant: boolean): void {
  const btn = document.getElementById('flag-btn');
  if (btn) {
    btn.classList.toggle('disabled', !canPlant);
  }
}

/** Round flows: the UNQUEUE button sits beside YIELD while the current unit has a shot queued. */
export function setUnqueueButton(visible: boolean): void {
  const btn = document.getElementById('unqueue-btn');
  if (btn) btn.style.display = visible ? '' : 'none';
}
